<template>
  <div class="pagination-shell" :class="shellClass">
    <div class="pagination-shell-inner">
    <div v-if="showInfo" class="pagination-info-group">
      <div v-if="showTotal">
        {{ totalLabel }}
        <span class="font-medium text-slate-700">{{ totalItems }}</span>
        {{ totalSuffix }}
      </div>

      <label v-if="showPageSize" class="pagination-size-group">
        <span v-if="pageSizeLabel">{{ pageSizeLabel }}</span>
        <select :value="pageSize" class="form-page-size" @change="handleSizeChange">
          <option v-for="option in normalizedPageSizes" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
        <span v-if="pageSizeSuffix">{{ pageSizeSuffix }}</span>
      </label>
    </div>

    <div v-if="showPager" class="pagination-pager-group">
      <button class="pagination-nav-button" :disabled="!canGoPrevious" @click="handlePageChange(currentPage - 1)">
        {{ prevText }}
      </button>

      <template v-if="mode === 'numbers'">
        <button
          v-for="page in visiblePages"
          :key="page"
          class="pagination-page-button"
          :class="{ 'is-active': page === currentPage }"
          :aria-current="page === currentPage ? 'page' : undefined"
          @click="handlePageChange(page)"
        >
          {{ page }}
        </button>
      </template>

      <template v-else>
        <span class="pagination-page-button is-active" aria-current="page">{{ currentPage }}</span>
        <span v-if="showPageCount && totalPages > 1" class="text-slate-500">/ {{ totalPages }}</span>
      </template>

      <button class="pagination-nav-button" :disabled="!canGoNext" @click="handlePageChange(currentPage + 1)">
        {{ nextText }}
      </button>
    </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  pagination: {
    type: Object,
    default: () => ({})
  },
  current: {
    type: Number,
    default: undefined
  },
  size: {
    type: Number,
    default: undefined
  },
  total: {
    type: Number,
    default: undefined
  },
  pages: {
    type: Number,
    default: undefined
  },
  hasPrevious: {
    type: Boolean,
    default: undefined
  },
  hasNext: {
    type: Boolean,
    default: undefined
  },
  pageSizes: {
    type: Array,
    default: () => [10, 20, 50]
  },
  mode: {
    type: String,
    default: 'numbers'
  },
  pageWindow: {
    type: Number,
    default: 2
  },
  showTotal: {
    type: Boolean,
    default: true
  },
  showPageSize: {
    type: Boolean,
    default: true
  },
  showPager: {
    type: Boolean,
    default: true
  },
  showPageCount: {
    type: Boolean,
    default: false
  },
  totalLabel: {
    type: String,
    default: '共 '
  },
  totalSuffix: {
    type: String,
    default: ' 条'
  },
  pageSizeLabel: {
    type: String,
    default: '每页'
  },
  pageSizeSuffix: {
    type: String,
    default: '条'
  },
  prevText: {
    type: String,
    default: '上一页'
  },
  nextText: {
    type: String,
    default: '下一页'
  }
})

const emit = defineEmits([
  'page-change',
  'size-change',
  'update:current',
  'update:page',
  'update:size'
])

const currentPage = computed(() => {
  const raw = props.current ?? props.pagination?.current ?? props.pagination?.page ?? 1
  return Math.max(1, Number(raw) || 1)
})

const pageSize = computed(() => {
  const fallback = Array.isArray(props.pageSizes) && props.pageSizes.length > 0 ? Number(props.pageSizes[0]) : 10
  const raw = props.size ?? props.pagination?.size ?? props.pagination?.pageSize ?? fallback
  return Math.max(1, Number(raw) || fallback)
})

const totalItems = computed(() => {
  const raw = props.total ?? props.pagination?.total ?? 0
  return Math.max(0, Number(raw) || 0)
})

const totalPages = computed(() => {
  const explicitPages = props.pages ?? props.pagination?.pages ?? props.pagination?.totalPages
  if (explicitPages !== undefined && explicitPages !== null && explicitPages !== '') {
    return Math.max(1, Number(explicitPages) || 1)
  }

  return Math.max(1, Math.ceil(totalItems.value / pageSize.value) || 1)
})

const canGoPrevious = computed(() => {
  if (props.hasPrevious !== undefined) return props.hasPrevious
  if (props.pagination?.hasPrevious !== undefined) return props.pagination.hasPrevious
  return currentPage.value > 1
})

const canGoNext = computed(() => {
  if (props.hasNext !== undefined) return props.hasNext
  if (props.pagination?.hasNext !== undefined) return props.pagination.hasNext
  return currentPage.value < totalPages.value
})

const visiblePages = computed(() => {
  const pages = []
  const start = Math.max(1, currentPage.value - props.pageWindow)
  const end = Math.min(totalPages.value, currentPage.value + props.pageWindow)

  for (let page = start; page <= end; page += 1) {
    pages.push(page)
  }

  return pages
})

const normalizedPageSizes = computed(() => (
  props.pageSizes
    .map(size => Number(size))
    .filter(size => Number.isFinite(size) && size > 0)
))

const showInfo = computed(() => props.showTotal || props.showPageSize)

const shellClass = computed(() => ({
  'is-simple': props.mode === 'simple',
  'has-info-only': showInfo.value && !props.showPager,
  'has-pager-only': props.showPager && !showInfo.value
}))

const handlePageChange = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return

  emit('update:current', page)
  emit('update:page', page)
  emit('page-change', page)
}

const handleSizeChange = (event) => {
  const size = Number(event?.target?.value || pageSize.value)
  if (!size || size === pageSize.value) return

  emit('update:size', size)
  emit('size-change', size)
}
</script>

